import { FC } from 'react';
import { BKJIconTooltip, BKJLoadingSpinner } from '@bookjane2/bookjane-design-library';
import { Flex } from 'components/Flex';
import { useTranslation } from 'hooks/useTranslation';
import { useSelector } from 'react-redux';
import isUserTimeFormat24hSelector from 'store/selectors/userSelectors/isUserTimeFormat24hSelector';
import { formatDateFromISO, formatTimeFromISO } from 'utils/time';
import { InfiniteScrollLoading } from 'components/InfiniteScrollLoading';
import {
  shiftModalRoutingService,
  useShiftModalUIContext,
} from 'modals/ShiftModal/ShiftModal.ui.context';
import {
  SectionLabel,
  ShiftCardList,
  ShiftCardWrapper,
  ShiftIdText,
  PositionText,
  CardText,
} from './ShiftBlockOrdersList.styled';
import { ShiftBlockOrder } from './ShiftBlockOrdersList.data.context';

interface ShiftBlockOrderCardProps {
  order: ShiftBlockOrder;
  is24hTimeFormat: boolean;
}

const ShiftBlockOrderCard: FC<ShiftBlockOrderCardProps> = ({ order, is24hTimeFormat }) => {
  const startTimeFormatted = order.orderSchedule?.startTime
    ? formatTimeFromISO(order.orderSchedule.startTime, is24hTimeFormat, order.timeZone)
    : '';
  const endTimeFormatted = order.orderSchedule?.endTime
    ? formatTimeFromISO(order.orderSchedule.endTime, is24hTimeFormat, order.timeZone)
    : '';
  const dateFormatted = order.startAt ? formatDateFromISO(order.startAt, order.timeZone) : '';
  const timeRange =
    startTimeFormatted && endTimeFormatted
      ? `${dateFormatted} at ${startTimeFormatted} - ${endTimeFormatted}`
      : dateFormatted;

  const location = order.subFacilityAddress?.name || order.community?.name || '';

  const { shift_id: currentShiftId } = useShiftModalUIContext();

  const handleClick = () => {
    if (!currentShiftId) return;
    shiftModalRoutingService.open({ shiftModalShiftId: order.relayId }, 'DetailView', {
      shiftModalShiftId: currentShiftId,
      shiftModalActiveModalView: 'DetailView',
    });
  };

  return (
    <ShiftCardWrapper onClick={handleClick}>
      <ShiftIdText>Shift #{order.orderSchedule.id}</ShiftIdText>
      <Flex flexDirection="column" gap="4px" flex="1" minWidth="0">
        <PositionText>{order.position?.name}</PositionText>
        {timeRange && <CardText>{timeRange}</CardText>}
        {location && <CardText>{location}</CardText>}
      </Flex>
    </ShiftCardWrapper>
  );
};

export interface ShiftBlockOrdersListProps {
  orders: ShiftBlockOrder[];
  isLoading: boolean;
  hasNextPage: boolean;
  loadMore: () => void;
  isAwarded?: boolean;
}

export const ShiftBlockOrdersList: FC<ShiftBlockOrdersListProps> = ({
  orders,
  isLoading,
  hasNextPage,
  loadMore,
  isAwarded = false,
}) => {
  const { t } = useTranslation();
  const is24hTimeFormat = useSelector(isUserTimeFormat24hSelector);

  const headingKey = isAwarded
    ? 'shift_modal:SHIFTS_AWARDED_WITH_THIS_BLOCK'
    : 'shift_modal:SHIFTS_IN_THIS_BLOCK';
  const tooltipKey = isAwarded
    ? 'shift_modal:SHIFTS_AWARDED_WITH_BLOCK_TOOLTIP'
    : 'shift_modal:SHIFTS_IN_BLOCK_TOOLTIP';

  return (
    <Flex flexDirection="column" gap="16px" flex="1" minHeight="0">
      <Flex alignItems="center" gap="8px">
        <SectionLabel>{t(headingKey)}</SectionLabel>
        <BKJIconTooltip iconName="Info" placement="top">
          <Flex width="200px">{t(tooltipKey)}</Flex>
        </BKJIconTooltip>
      </Flex>
      <ShiftCardList>
        {isLoading ? (
          <Flex justifyContent="center" padding="24px">
            <BKJLoadingSpinner variant="Default" />
          </Flex>
        ) : (
          orders.map((order) => (
            <ShiftBlockOrderCard
              key={order.displayId}
              order={order}
              is24hTimeFormat={is24hTimeFormat}
            />
          ))
        )}
        {hasNextPage && <InfiniteScrollLoading onViewportEnter={loadMore} />}
      </ShiftCardList>
    </Flex>
  );
};
